import type { Getter, Row } from "@tanstack/react-table";
import type { AxiosError } from "axios";
import Axios from "axios";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";
import useSWR from "swr";
import {
  PrimaryButtonLargeV2,
  LinkButtonMediumV2,
  TertiaryButtonLargeV2,
  ButtonContainer,
} from "../../../../components/Buttons/Buttons";
import { DropDown } from "../../../../components/DropDown/DropDown";
import {
  LinkIcon,
  MagicDocIcon,
  SearchIcon,
  XIcon,
} from "../../../../components/Icons/Icons";
import { Notifications } from "../../../../components/Notifications/NotificationsContext";
import { Pagination } from "../../../../components/Pagination/Pagination";
import { SingleSelectNoController } from "../../../../components/SingleSelectNoController/SingleSelectNoController";
import { Table } from "../../../../components/Table/Table";
import type { TableRefProps } from "../../../../components/Table/utils";
import { H6 } from "../../../../components/Typography/Typography";
import { endpoints } from "../../../../endpoints";
import type {
  DataMutate,
  OptionType,
  SupportedLanguage,
} from "../../../../types/types";
import type {
  Assets,
  PIMAssetsResponse,
  PIMProduct,
  SupportedAssetType,
  PIMProductStatusAck,
} from "../../../../types/types.PIM";
import { useDebounce } from "../../../../util/hooks";
import {
  isAxiosError,
  useStoreState,
  useUpdateProductStatus,
} from "../../../../util/util";
import { GridAssetView } from "../../../admin/SellerAdmin/PIM/SellarAdminPIMAssets/GridAssetView/GridAssetView";
import type { AssetCategory } from "../../../admin/SellerAdmin/PIM/SellarAdminPIMAssets/util/AssetsUtil";
import {
  GetDocumentIcon,
  TrimmedName,
  get_content_type,
} from "../../../admin/SellerAdmin/PIM/SellarAdminPIMAssets/util/AssetsUtil";
import { spacingTokens } from "../../../../designTokens";

const TableContainer = styled.div`
  align-self: stretch;
  flex-grow: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
  padding: ${({ theme }) => theme.resolve("spacing_lg")};
  border-radius: ${({ theme }) => theme.resolve("radius_md")};
  border: solid ${({ theme }) => theme.resolve("border_md")}
    ${({ theme }) => theme.colors.secondaryLightGray};
`;

const ScrollArea = styled.div`
  max-height: 460px;
  overflow-y: auto;
  display: block;
`;

const SearchWrapper = styled.div`
  position: relative;
  display: inline-block;
  width: 100%;
  min-height: 52px;
  margin-bottom: 28px;
  > svg {
    position: absolute;
    right: 13px;
    top: 14px;
    path {
      fill: #9fa4aa;
    }
  }
`;

const FilterDropdownContainer = styled.div`
  .view_type,
  .category_type {
    z-index: 2;
  }
`;

const InnerTitleText = styled.div`
  color: ${({ theme }) => theme.secondaryTextColor};
  font-family: ${({ theme }) => theme.fontFamily};
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: normal;
  text-align: right;
`;

const FlexWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
`;

const SearchInput = styled.input`
  box-sizing: border-box;
  width: 100%;
  min-height: 52px;
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.primaryBorder};
  font-family: Inter;
  padding: 10px 13px 10px 13px;
  font-size: 13px;

  &:focus {
    border: ${({ theme }) => theme.resolve("border_md")} solid
      ${({ theme }) => theme.tertiaryBorder};
    box-shadow: 0 0 1px 1px ${({ theme }) => theme.tertiaryBorder};
    outline: none;
  }
`;

const ClearInput = styled.span`
  position: absolute;
  top: 14px;
  right: 13px;
  cursor: pointer;
`;

type QueryType = {
  q?: string;
};

export const ExistingAssets = <ParentData,>({
  title,
  onComplete,
  onCancel,
  onAddAssets,
  product,
  products,
  languages,
  fetchParentData,
  showGeneratedAssets,
  allowUpload = false,
}: {
  title: string;
  product: PIMProduct;
  onComplete?: (productId?: string) => void;
  onCancel?: () => void;
  fetchParentData: DataMutate<ParentData>;
  showGeneratedAssets: boolean;
  onAddAssets?: ({
    selected_assets,
    selected_product,
    selected_language,
  }: {
    selected_assets: Assets[];
    selected_product?: OptionType<string>;
    selected_language?: OptionType<SupportedLanguage>;
  }) => Promise<void>;
  products?: OptionType<string>[];
  languages?: OptionType<SupportedLanguage>[];
  allowUpload?: boolean;
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [categoryType, setCategoryType] = useState<string>("All");
  const [viewType, setViewType] = useState<"list" | "grid">("list");
  const [categoryTypes, setCategoryTypes] = useState<string[]>([]);
  const [perPage] = useState(20);
  const [tablePagination, setTablePagination] = useState({
    perPage: perPage,
    pageCount: 0,
    pageIndex: 0,
  });
  const [offset, setOffset] = useState(0);
  const [query, setQuery] = useState<QueryType>();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 1000);
  const [selectedItems, setSelectedItems] =
    useState<Record<number, Array<Assets>>>();
  const [selectedRows, setSelectedRows] = useState<string[] | "all">();
  const [selected_product, set_selected_product] =
    useState<OptionType<string>>();
  const [selected_language, set_selected_language] =
    useState<OptionType<SupportedLanguage>>();
  const [tableRef, setTableRef] = useState<Record<string, () => void>>();
  const [tableData, setTableData] = useState<Assets[]>([]);

  const resetSelectedRowsRef = useRef<TableRefProps>(null);
  const gridAssetViewRef = useRef<TableRefProps>(null);

  const { tenant_id } = useStoreState();
  const { t } = useTranslation();
  const { notifySuccess, notifyError } = useContext(Notifications);

  const updateProductStatus = useUpdateProductStatus({
    product,
  });

  function constructQuery({
    baseURL,
    queryParams,
    asset_type,
    asset_category,
  }: {
    baseURL: string;
    queryParams?: QueryType;
    asset_type: string | string[];
    asset_category: string | string[];
  }) {
    const params = new URLSearchParams();
    if (queryParams?.q) {
      params.append("q", queryParams?.q);
    }
    if (asset_category !== "All") {
      if (Array.isArray(asset_category)) {
        asset_category.forEach((category) =>
          params.append("asset_category", category)
        );
      } else {
        params.append("asset_category", asset_category);
      }
    }
    if (Array.isArray(asset_type)) {
      asset_type.forEach((type) => params.append("asset_type", type));
    } else {
      params.append("asset_type", asset_type);
    }
    params.append("limit", `${perPage}`);
    params.append("offset", `${offset}`);
    params.append("show_generated_assets", showGeneratedAssets.toString());
    return baseURL + "?" + params;
  }

  const { data: assetsResponse, error: assetsError } =
    useSWR<PIMAssetsResponse>(
      constructQuery({
        baseURL: endpoints.v2_storefronts_id_pim_assets(tenant_id),
        queryParams: query,
        asset_type: title === "Documents" ? "document" : ["video", "image"],
        asset_category:
          categoryType === "All"
            ? categoryTypes.length > 0
              ? categoryTypes.slice(1) // Category type: ["All", "Marketing", ...]. This removes the "All", as it isn't recognized from the BE
              : "All"
            : categoryType,
      })
    );

  const assetTypeParams = (() => {
    const params = new URLSearchParams();
    if (title === "Documents") {
      params.append("asset_type", "document");
    } else {
      ["video", "image"].forEach((type) => {
        params.append("asset_type", type);
      });
    }
    return params;
  })();
  const { error: categoryTypeError } = useSWR<AssetCategory>(
    `${endpoints.v2_tenants_id_pim_assets_categories(
      tenant_id
    )}?${assetTypeParams}&deduped=true`,
    {
      onSuccess: ({ asset_categories }) =>
        setCategoryTypes(["All", ...asset_categories.map(({ name }) => name)]),
    }
  );

  const isLoading = !assetsResponse && !assetsError;

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setQuery({ q: "" });
  };

  const changeFilterType = (categoryType: string) => {
    setCategoryType(categoryType);
    resetSelectedRowsRef?.current?.resetSelectedRows();
  };

  const changeViewType = (view_type: string) => {
    setViewType(view_type === t("List") ? "list" : "grid");
  };

  const changePage = (offset: number) => {
    setOffset(offset);
    setTableData([]);
  };

  const tableColumns = React.useMemo(
    () => [
      {
        header: t("Asset Name"),
        accessorKey: "name",
        cell: ({
          getValue,
          row: {
            original: { asset_type, is_generated, content_type, asset_mode },
          },
        }: {
          getValue: Getter<string>;
          row: {
            original: {
              asset_type: string;
              is_generated: boolean;
              content_type: string | null;
              asset_mode: Assets["asset_mode"];
            };
          };
        }) => {
          return (
            <FlexWrapper>
              {is_generated ? (
                <div
                  data-tip={t("Generated Document")}
                  data-for="generated-doc-tooltip"
                >
                  <MagicDocIcon />
                  <ReactTooltip effect="solid" id="generated-doc-tooltip" />
                </div>
              ) : asset_mode === "external" ? (
                <LinkIcon width={24} height={24} />
              ) : (
                <div>
                  <GetDocumentIcon
                    asset_type={asset_type as SupportedAssetType}
                    file_type={get_content_type(
                      content_type ?? "application/pdf"
                    )}
                  />
                </div>
              )}
              <TrimmedName text={getValue()} />
            </FlexWrapper>
          );
        },
      },
      {
        header: t("Category"),
        accessorKey: "asset_category",
      },
    ],
    [t]
  );

  const handleSelectRows = (rows: string[] | "all") => {
    if (rows) {
      setSelectedRows(rows);
    }
  };

  const default_submit = async () => {
    try {
      const { data: ackedProduct }: { data: PIMProductStatusAck } =
        await updateProductStatus();
      await Axios.patch(
        endpoints.v2_tenants_tenant_id_pim_products_product_id_assets_associate(
          tenant_id,
          ackedProduct.id
        ),
        {
          asset_ids: Object.values(selectedItems ?? {})
            .flat()
            .map(({ id }) => id),
        }
      );
      notifySuccess(t("Assets successfully added"));
      if (onComplete) {
        onComplete(ackedProduct?.primary_staged_product_id ?? ackedProduct?.id);
      }
      await fetchParentData();
    } catch (error) {
      if (
        isAxiosError(error) &&
        (error as AxiosError)?.response?.data?.message
      ) {
        notifyError(error?.response?.data?.message);
      } else {
        notifyError(t("could not add an asset, Something went wrong."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedItems || Object.keys(selectedItems).length === 0) {
      notifyError(t("Please select an asset and continue"));
    } else {
      setSubmitting(true);
      if (onAddAssets) {
        await onAddAssets({
          selected_assets: Object.values(selectedItems).flat(),
          selected_product,
          selected_language,
        });
        setSubmitting(false);
      } else {
        default_submit();
      }
    }
  };

  const selectProduct = (selected_product: OptionType<string>) => {
    set_selected_product(selected_product);
  };

  const selectLanguage = (selected_language: OptionType<SupportedLanguage>) => {
    set_selected_language(selected_language);
  };

  const onResetSelectedRows = () => {
    if (resetSelectedRowsRef?.current) {
      resetSelectedRowsRef?.current.resetSelectedRows();
    } else {
      tableRef?.["resetSelectedRows"]();
    }
    gridAssetViewRef?.current?.resetSelectedRows();
  };

  useEffect(() => {
    if (selectedRows && selectedRows !== "all" && tableData.length) {
      const selected_rows_obj = selectedRows.reduce<Record<string, boolean>>(
        (acc, id) => ({ ...acc, [id]: true }),
        {}
      );
      setSelectedItems((prev) => {
        const updated_selected_items = prev ?? {};
        if (selectedRows.length) {
          updated_selected_items[tablePagination.pageIndex] = tableData.filter(
            ({ id }) => selected_rows_obj[id]
          );
        } else {
          Object.keys(updated_selected_items).forEach((key) => {
            updated_selected_items[Number(key)] = [];
          });
        }
        return updated_selected_items;
      });
    }
  }, [selectedRows, tableData, tablePagination.pageIndex]);

  useEffect(() => {
    const handleProductsData = ({ data, pagination }: PIMAssetsResponse) => {
      setTableData(
        data.map(
          ({
            id,
            name,
            asset_category,
            category,
            language,
            asset_type,
            is_generated,
            content_type,
            preview_url,
            asset_mode,
            host,
          }) =>
            ({
              id,
              name: name ?? "--",
              category,
              asset_category,
              language,
              asset_type,
              is_generated,
              content_type,
              preview_url,
              asset_mode,
              host,
            } as Assets)
        )
      );
      setTablePagination({
        perPage,
        pageCount: Math.ceil(pagination.total / perPage),
        pageIndex: pagination.offset / perPage + 1,
      });
    };

    if (assetsResponse) {
      const { data: assets, pagination } = assetsResponse;
      handleProductsData({ data: assets, pagination });
    }
  }, [assetsResponse, perPage, title]);

  useEffect(() => {
    if (debouncedSearchQuery === "") setQuery({ q: undefined });
    if (debouncedSearchQuery) {
      setQuery({ q: debouncedSearchQuery });
    }
  }, [setQuery, debouncedSearchQuery]);

  useEffect(() => {
    if (resetSelectedRowsRef?.current) {
      setTableRef(resetSelectedRowsRef.current);
    }
  }, []);

  const has_selected_assets = selectedRows && selectedRows.length > 0;
  return (
    <>
      <div
        style={{
          margin: `${spacingTokens.spacing_3xl} ${spacingTokens.spacing_none} ${spacingTokens.spacing_xl} ${spacingTokens.spacing_none}`,
        }}
      >
        <H6>{t("Choose assets")}</H6>
      </div>
      <SearchWrapper>
        <SearchInput
          placeholder={t("Search by name")}
          name="asset_name"
          onChange={handleSearch}
          value={searchQuery}
        />
        {searchQuery ? (
          <ClearInput onClick={handleClearSearch}>
            <XIcon width={20} height={20} />
          </ClearInput>
        ) : (
          <SearchIcon />
        )}
      </SearchWrapper>
      <TableContainer>
        <FlexWrapper style={{ justifyContent: "space-between" }}>
          <InnerTitleText>
            {selectedRows && selectedRows.length > 0 ? (
              <div
                style={{ display: "flex", gap: "5px", alignItems: "center" }}
              >
                <span style={{ textAlign: "center" }}>
                  {t("{{selected_items_count}} selected", {
                    selected_items_count: selectedRows.length,
                  })}
                </span>
                <LinkButtonMediumV2 onClick={onResetSelectedRows}>
                  {t("Unselect All")}
                </LinkButtonMediumV2>
              </div>
            ) : (
              t("Showing {{numOfAssets}} assets", {
                numOfAssets: assetsResponse?.pagination?.total ?? 0,
              })
            )}
          </InnerTitleText>
          <div style={{ display: "flex" }}>
            <FilterDropdownContainer>
              <DropDown
                items={[t("List") as string, t("Grid") as string]}
                activeItem={viewType === "list" ? t("List") : t("Grid")}
                textLeft={t("View") + ":"}
                direction={"left"}
                className={"view_type"}
                clickHandler={changeViewType}
              ></DropDown>
            </FilterDropdownContainer>
            {!categoryTypeError && (
              <FilterDropdownContainer>
                <DropDown
                  items={categoryTypes}
                  activeItem={categoryType}
                  textLeft={t("Filter") + ":"}
                  direction={"left"}
                  className={"category_type"}
                  clickHandler={changeFilterType}
                ></DropDown>
              </FilterDropdownContainer>
            )}
          </div>
        </FlexWrapper>
        <ScrollArea>
          {viewType === "list" ? (
            <Table
              columns={tableColumns}
              data={tableData}
              isLoading={isLoading}
              error={assetsError}
              handleSelectRows={handleSelectRows}
              defaultSelectedRows={selectedRows}
              ref={resetSelectedRowsRef}
            />
          ) : (
            <GridAssetView
              assets={
                assetsResponse?.data
                  ? assetsResponse.data.map((asset) => ({
                      ...asset,
                      can_edit: false,
                      can_change_visibility: false,
                      can_delete: false,
                      can_external_download: true,
                      can_view_details: true,
                    }))
                  : []
              }
              on_download_asset={(asset) => {
                window.open(asset.signed_url, "_blank");
              }}
              handleSelectedItems={(items) => {
                handleSelectRows(items.map(({ original: { id } }) => id));
              }}
              selectedItems={Object.values(selectedItems ?? {})
                .flat()
                .map((item) => ({ original: item } as Row<Assets>))}
              is_existing_asset_page={true}
              productSlug={product.slug}
              ref={gridAssetViewRef}
            />
          )}
        </ScrollArea>
        <Pagination
          miniPagination={true}
          pagination={tablePagination}
          offset={offset}
          handlePageClick={changePage}
        />
      </TableContainer>
      {languages && (
        <SingleSelectNoController
          options={languages}
          name="language"
          placeholder={t("Language")}
          handleChange={selectLanguage}
          {...(languages.length === 1 ? { defaultValue: languages[0] } : {})}
        />
      )}
      {products && (
        <SingleSelectNoController
          options={products}
          name="products"
          placeholder={t("Assign to product")}
          handleChange={selectProduct}
          {...(products.length === 1 ? { defaultValue: products[0] } : {})}
        />
      )}
      <ButtonContainer style={{ marginTop: "30px" }}>
        <TertiaryButtonLargeV2
          fullWidth
          disabled={submitting}
          type="button"
          onClick={() => onCancel?.()}
        >
          {t("Cancel")}
        </TertiaryButtonLargeV2>
        <PrimaryButtonLargeV2
          type="submit"
          fullWidth
          onClick={handleSubmit}
          loading={submitting}
          disabled={!allowUpload && !has_selected_assets}
          datatip={
            has_selected_assets ? "" : t("Please select an asset and continue")
          }
          datafor={has_selected_assets ? "" : "save-button-tooltip"}
        >
          {t("Save")}
        </PrimaryButtonLargeV2>
      </ButtonContainer>
      <ReactTooltip effect="solid" id="save-button-tooltip" />
    </>
  );
};
